import * as yup from "yup";

export type InductionConfigType =
  | "ORGANIZATION"
  | "BRANCH"
  | "BUSINESS"
  | "DEPARTMENT"
  | "POLICY";

export interface IInductionPolicy {
  id?: number;
  config_Type: InductionConfigType;
  company_Id: number | null;
  branch_Id: number | null;
  department_Id: number | null;
  business_Process_Id: number | null;
  company_Ids?: number[];
  branch_Ids?: number[];
  department_Ids?: number[];
  business_Process_Ids?: number[];
  policy_Key: string;
  policy_Title: string;
  policy_Content: string;
  order_Index: number;
  is_Active: boolean;
  video_Url?: string | null;

}

export const emptyInductionPolicy: IInductionPolicy = {
  config_Type: "ORGANIZATION",
  company_Id: null,
  branch_Id: null,
  department_Id: null,
  business_Process_Id: null,
  company_Ids: [],
  branch_Ids: [],
  department_Ids: [],
  business_Process_Ids: [],
  policy_Key: "Vision",
  policy_Title: "Company Vision",
  policy_Content: "",
  order_Index: 1,
  is_Active: true,
  video_Url: "",

};

export interface IPolicyPreset {
  key: string;
  title: string;
  badgeScheme: string;
  description: string;
  defaultContent: string;
  type: InductionConfigType;
}

// Section 1: Company Profile Presets
export const organizationPresets: IPolicyPreset[] = [
  {
    type: "ORGANIZATION",
    key: "Vision",
    title: "Company Vision",
    badgeScheme: "teal",
    description: "Long-term organizational vision and aspirations presented on Step 1",
    defaultContent:
      "To be the most trusted technology and talent partner for growing businesses worldwide, recognized for unwavering innovation and client success.",
  },
  {
    type: "ORGANIZATION",
    key: "Mission",
    title: "Company Mission",
    badgeScheme: "purple",
    description: "Core mission statement and commitment presented on Step 1",
    defaultContent:
      "Deliver dependable people, products, and operational excellence to help clients and employees flourish in an ever-evolving global market.",
  },
  {
    type: "ORGANIZATION",
    key: "About Company",
    title: "Company Overview",
    badgeScheme: "blue",
    description: "Introductory overview of the organization and service verticals",
    defaultContent:
      "An integrated partner providing staffing, technology solutions, and digital consulting across global markets under a unified culture of excellence.",
  },
  {
    type: "ORGANIZATION",
    key: "Core Values",
    title: "Our Core Values",
    badgeScheme: "cyan",
    description: "Foundational values and principles guiding employee behavior",
    defaultContent:
      "Integrity in all partnerships, Customer-first commitment, Continuous learning and innovation, and Empathy across diverse teams.",
  },
];

// Section 2: Branch Profile Presets
export const branchPresets: IPolicyPreset[] = [
  {
    type: "BRANCH",
    key: "Branch Intro",
    title: "Branch Overview & Welcome",
    badgeScheme: "green",
    description: "Introduction text read when entering the Branch induction step",
    defaultContent:
      "Welcome to this regional operations hub. This branch houses dedicated teams, state-of-the-art infrastructure, and collaborative working spaces.",
  },
  {
    type: "BRANCH",
    key: "Facilities",
    title: "Branch Facilities & Amenities",
    badgeScheme: "blue",
    description: "Cafeteria, conference rooms, high-speed connectivity, and recreational zones",
    defaultContent:
      "Equipped with modern workstations, ergonomic seating, dedicated meeting hubs, cafeteria facilities, and 24/7 power and high-speed fiber internet backup.",
  },
  {
    type: "BRANCH",
    key: "Office Timings",
    title: "Branch Operating Hours & Shifts",
    badgeScheme: "orange",
    description: "Official shift timing and facility access hours for this branch",
    defaultContent:
      "General operating hours are 9:30 AM to 6:30 PM, Monday through Friday. Security access is enabled from 8:00 AM to 10:00 PM on working days.",
  },
  {
    type: "BRANCH",
    key: "Safety & Security",
    title: "Access Control & Security Protocol",
    badgeScheme: "red",
    description: "ID badge rules, visitor entry protocols, and premises safety guidelines",
    defaultContent:
      "Access badges must be worn visibly at all times within the premises. Visitors must register at the reception desk before entering work floors.",
  },
];

// Section 3: Business Vertical Presets
export const businessPresets: IPolicyPreset[] = [
  {
    type: "BUSINESS",
    key: "Business Intro",
    title: "Vertical Overview & Mission",
    badgeScheme: "purple",
    description: "Overview text explaining the business vertical and market mandate",
    defaultContent:
      "This business vertical delivers high-impact specialized solutions, supporting global clients with dedicated execution teams and measurable deliverables.",
  },
  {
    type: "BUSINESS",
    key: "Operational Focus",
    title: "Core Operational Mandate",
    badgeScheme: "teal",
    description: "Primary day-to-day focus, capabilities, and delivery methodology",
    defaultContent:
      "Delivering scalable, compliant, and cost-efficient client engagements through rigorous quality benchmarks and continuous performance evaluation.",
  },
  {
    type: "BUSINESS",
    key: "Client Collaboration",
    title: "Client Engagement & Delivery SLAs",
    badgeScheme: "cyan",
    description: "Expectations around SLA adherence, client communication, and turnaround time",
    defaultContent:
      "Upholding prompt, transparent communication with client stakeholders and ensuring all service deliverables meet strict turnaround and confidentiality standards.",
  },
  {
    type: "BUSINESS",
    key: "Quality Standards",
    title: "Quality Benchmarks & Continuous Growth",
    badgeScheme: "blue",
    description: "Quality assurance standards, audits, and performance excellence expectations",
    defaultContent:
      "Adhering to ISO and industry quality protocols, regular peer reviews, and ongoing process enhancement to maintain market leadership.",
  },
];

// Section 4: Department Presets
export const departmentPresets: IPolicyPreset[] = [
  {
    type: "DEPARTMENT",
    key: "Department Intro",
    title: "Department Overview & Welcome",
    badgeScheme: "blue",
    description: "Introduction text read when entering the Department induction step",
    defaultContent:
      "Welcome to your department. Here you collaborate with specialized peers, reporting managers, and lead professionals driving core outcomes.",
  },
  {
    type: "DEPARTMENT",
    key: "Department Objectives",
    title: "Department Objectives & Annual Goals",
    badgeScheme: "teal",
    description: "Strategic priorities, quarterly milestones, and success metrics for this department",
    defaultContent:
      "Driven by quarterly OKRs focusing on project excellence, team skill expansion, process automation, and timely delivery across assigned initiatives.",
  },
  {
    type: "DEPARTMENT",
    key: "Tools & Technologies",
    title: "Core Tooling & Systems Stack",
    badgeScheme: "purple",
    description: "Key platforms, software, and internal systems used across this department",
    defaultContent:
      "Teams leverage standardized enterprise tooling, version control, project tracking boards, and HRlense portal for all daily operations and attendance.",
  },
  {
    type: "DEPARTMENT",
    key: "Team Communication",
    title: "Collaboration & Team Rhythm",
    badgeScheme: "orange",
    description: "Daily standup cadence, sync meetings, and communication etiquette",
    defaultContent:
      "Active collaboration via daily scrums, weekly reviews, and instant team messaging channels to maintain alignment and rapid roadblock resolution.",
  },
];

// Section 5: HR Policies Presets
export const policyPresets: IPolicyPreset[] = [
  {
    type: "POLICY",
    key: "Leave Policy",
    title: "Paid Leave & Time-off Policy",
    badgeScheme: "green",
    description: "Annual leave quota, carry-forward limits, and advance notice rules",
    defaultContent:
      "Employees are entitled to 24 paid leaves per fiscal year, accrued at 2 days per completed month of service. Leave applications must be submitted on HRlense at least 2 working days in advance, except in medical emergency.",
  },
  {
    type: "POLICY",
    key: "Notice Period",
    title: "Notice Period & Separation Norms",
    badgeScheme: "orange",
    description: "Mandatory notice days, buyout conditions, and handover formalities",
    defaultContent:
      "Confirmed employees must serve a 30-day notice period upon resignation. Resignation requests must be raised via the HRlense portal with clearance from reporting manager and IT asset handover.",
  },
  {
    type: "POLICY",
    key: "Attendance Rules",
    title: "Daily Attendance & Punch In/Out",
    badgeScheme: "blue",
    description: "Biometric & geofenced punch rules, regularization limits",
    defaultContent:
      "All employees must mark attendance daily using HRlense mobile or web punch. Missed or erratic punches must be regularized within 3 calendar days, subject to manager approval.",
  },
  {
    type: "POLICY",
    key: "Late Coming Policy",
    title: "Punctuality & Grace Period Norms",
    badgeScheme: "yellow",
    description: "Daily grace period, consecutive late deductions",
    defaultContent:
      "A 15-minute grace period is permitted past official reporting time. Three occurrences of late punch-ins in a single calendar month will automatically count as a half-day deduction.",
  },
  {
    type: "POLICY",
    key: "Work From Home Policy",
    title: "Remote & Hybrid Working Policy",
    badgeScheme: "teal",
    description: "WFH request protocol, connectivity standards, and availability",
    defaultContent:
      "Remote working days must be requested in advance via the portal. Employees are expected to maintain stable high-speed broadband and be accessible via Slack/Teams during core business hours.",
  },
  {
    type: "POLICY",
    key: "Company Asset Policy",
    title: "Hardware & Device Custody Policy",
    badgeScheme: "purple",
    description: "Laptop, peripheral handling, loss liability, and security",
    defaultContent:
      "Company hardware and IT peripherals remain company property. Any loss, damage, or malfunction must be reported to the IT Helpdesk within 24 hours.",
  },
  {
    type: "POLICY",
    key: "Code of Conduct",
    title: "Workplace Ethics & Code of Conduct",
    badgeScheme: "pink",
    description: "Integrity, anti-harassment (POSH), client NDA, and peer respect",
    defaultContent:
      "Employees must uphold zero tolerance for harassment, maintain client data confidentiality, prevent conflict of interest, and foster an inclusive, respectful work culture.",
  },
];

export const inductionPolicySchema = yup.object({
  config_Type: yup
    .string()
    .oneOf(["ORGANIZATION", "BRANCH", "BUSINESS", "DEPARTMENT", "POLICY"])
    .default("ORGANIZATION"),
  company_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  branch_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  department_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  business_Process_Id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" || o === undefined || o === 0 ? null : v)),
  policy_Key: yup
    .string()
    .required("Category or Tag identifier is required")
    .trim()
    .min(2, "Category must be at least 2 characters")
    .max(80, "Category cannot exceed 80 characters"),
  policy_Title: yup
    .string()
    .required("Display Title is required")
    .trim()
    .min(2, "Title must be at least 2 characters")
    .max(150, "Title cannot exceed 150 characters"),
  policy_Content: yup
    .string()
    .required("Content / Guidelines text is required")
    .trim()
    .min(10, "Content must be at least 10 characters")
    .max(4000, "Content cannot exceed 4000 characters"),
  order_Index: yup
    .number()
    .typeError("Order sequence must be a valid number")
    .required("Display sequence order is required")
    .min(1, "Order sequence must be at least 1")
    .max(999, "Order sequence cannot exceed 999"),
  is_Active: yup.boolean().default(true),
  video_Url: yup.string().nullable().notRequired(),
});
